// src/pages/Analise/AnaliseAutoCreateModal.jsx
// Fluxo AUTOMÁTICO de criação de análise para clientes SANEAMENTO (1 protocolo com
// Afluente/Efluente). Gera o código (yyMMdd-HHmm), busca a temperatura do clima local,
// auto-seleciona TODOS os dispositivos livres de cada grupo, deixa o usuário conferir/editar
// a diluição de cada um e cria com um clique. Modal enxuto de confirmação.
import React, { useEffect, useMemo, useState } from 'react';
import { presetDiluicaoTorre } from '../../utils/diluicao';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    Box,
    Stack,
    Typography,
    IconButton,
    Paper,
    Grid,
    Chip,
    Divider,
    Alert,
    CircularProgress,
    useTheme,
    useMediaQuery,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import * as Lucide from 'lucide-react';

import { getTowers } from '../../infra/api/Torres';
import { useBootstrap } from '../../context/BootstrapContext';
import DilutionInput from './DilutionInput';
import {
    composeLocationQuery,
    searchAddressForward,
    fetchLocalWeather,
    buildLocationSummary,
} from './analiseLocation';

const normalizeId = (val) => {
    if (!val) return '';
    if (typeof val === 'string') {
        const m = val.match(/ObjectId\("([^"]+)"\)/);
        if (m) return m[1];
        try {
            const parsed = JSON.parse(val);
            if (parsed?.$oid) return String(parsed.$oid);
        } catch (e) {}
        return val;
    }
    if (typeof val === 'object' && val?.$oid) return String(val.$oid);
    return String(val);
};

const pad2 = (n) => String(n).padStart(2, '0');

// Código no formato yyMMdd-HHmm a partir do horário local (ordena naturalmente).
const buildAutoCodigo = (date = new Date()) => {
    const yy = pad2(date.getFullYear() % 100);
    const mm = pad2(date.getMonth() + 1);
    const dd = pad2(date.getDate());
    const hh = pad2(date.getHours());
    const min = pad2(date.getMinutes());
    return `${yy}${mm}${dd}-${hh}${min}`;
};

const towerId = (t) => normalizeId(t?.id || t?._id);

export default function AnaliseAutoCreateModal({ open, onClose, onCreate }) {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

    const { bootstrap } = useBootstrap();
    const protocolos = useMemo(
        () => bootstrap?.protocolos || bootstrap?.protocols || [],
        [bootstrap]
    );

    // Detecta o modo SANEAMENTO: 1 protocolo com exatamente Afluente + Efluente.
    const special = useMemo(() => {
        if (protocolos.length !== 1) return null;
        const proto = protocolos[0];
        const analises = proto.analises || proto.analysis || [];
        if (analises.length !== 2) return null;
        const afluente = analises.find((a) =>
            String(a.name || a.nome || '')
                .toLowerCase()
                .includes('afluente')
        );
        const efluente = analises.find((a) =>
            String(a.name || a.nome || '')
                .toLowerCase()
                .includes('efluente')
        );
        if (!afluente || !efluente) return null;
        return {
            protocoloId: normalizeId(proto.id || proto._id),
            protocoloNome: String(proto.name || proto.nome || '').trim(),
            afluenteId: normalizeId(afluente.id || afluente._id),
            efluenteId: normalizeId(efluente.id || efluente._id),
        };
    }, [protocolos]);

    const [codigo, setCodigo] = useState('');
    const [temperatura, setTemperatura] = useState(null);
    const [location, setLocation] = useState(null);
    const [tempLoading, setTempLoading] = useState(false);

    const [loadingTowers, setLoadingTowers] = useState(false);
    const [afluente, setAfluente] = useState([]);
    const [efluente, setEfluente] = useState([]);
    const [dilutions, setDilutions] = useState({}); // torreId -> 0..6

    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState('');

    // (Re)inicializa tudo ao abrir.
    useEffect(() => {
        if (!open) return;
        let cancelled = false;

        setCodigo(buildAutoCodigo());
        setTemperatura(null);
        setLocation(null);
        setError('');
        setAfluente([]);
        setEfluente([]);
        setDilutions({});

        // 1) Temperatura via clima local (silencioso; se falhar, o backend preenche).
        const client = bootstrap?.client;
        const query = client ? composeLocationQuery(client) : '';
        if (query) {
            setTempLoading(true);
            searchAddressForward(query)
                .then(async (coords) => {
                    if (cancelled) return;
                    if (coords) {
                        setLocation({
                            latitude: coords.latitude,
                            longitude: coords.longitude,
                        });
                        const t = await fetchLocalWeather(
                            coords.latitude,
                            coords.longitude
                        );
                        if (!cancelled && t !== null) setTemperatura(t);
                    }
                })
                .finally(() => {
                    if (!cancelled) setTempLoading(false);
                });
        }

        // 2) Auto-seleção: TODOS os livres de cada grupo.
        if (special?.protocoloId) {
            setLoadingTowers(true);
            Promise.all([
                special.afluenteId
                    ? getTowers(0, 200, {
                          protocoloId: special.protocoloId,
                          analiseId: special.afluenteId,
                          running: false,
                      })
                    : Promise.resolve({ content: [] }),
                special.efluenteId
                    ? getTowers(0, 200, {
                          protocoloId: special.protocoloId,
                          analiseId: special.efluenteId,
                          running: false,
                      })
                    : Promise.resolve({ content: [] }),
            ])
                .then(([pa, pe]) => {
                    if (cancelled) return;
                    const la = Array.isArray(pa?.content) ? pa.content : [];
                    const le = Array.isArray(pe?.content) ? pe.content : [];
                    setAfluente(la);
                    setEfluente(le);
                    const d = {};
                    [...la, ...le].forEach((t) => {
                        // Preset do dispositivo (?? e nao ||: 0 e preset valido, "pura").
                        d[towerId(t)] = presetDiluicaoTorre(t) ?? 0;
                    });
                    setDilutions(d);
                })
                .catch(() => {
                    if (!cancelled) {
                        setAfluente([]);
                        setEfluente([]);
                    }
                })
                .finally(() => {
                    if (!cancelled) setLoadingTowers(false);
                });
        }

        return () => {
            cancelled = true;
        };
    }, [open, special, bootstrap]);

    const locationLabel = useMemo(() => {
        const s = buildLocationSummary({
            neighborhood: bootstrap?.client?.neighborhood,
            city: bootstrap?.client?.city,
            state: bootstrap?.client?.state,
        });
        return [s.city, s.neighborhood].filter(Boolean).join(' - ');
    }, [bootstrap]);

    const setDilucaoFor = (id, val) =>
        setDilutions((prev) => ({ ...prev, [id]: val }));

    const totalDispositivos = afluente.length + efluente.length;
    const canSubmit =
        !!special &&
        !submitting &&
        !loadingTowers &&
        totalDispositivos > 0 &&
        afluente.length > 0 &&
        efluente.length > 0;

    const handleCreate = async () => {
        if (!canSubmit) return;
        setSubmitting(true);
        setError('');

        const torres = [...afluente, ...efluente].map((t) => ({
            torreId: towerId(t),
            diluicao: Number(dilutions[towerId(t)] || 0),
        }));

        const payload = {
            identificacao: codigo,
            protocolosAnalises: [
                {
                    protocoloId: special.protocoloId,
                    protocoloNome: special.protocoloNome || undefined,
                    analiseId: special.afluenteId || undefined,
                    analiseNome: 'Afluente',
                },
                {
                    protocoloId: special.protocoloId,
                    protocoloNome: special.protocoloNome || undefined,
                    analiseId: special.efluenteId || undefined,
                    analiseNome: 'Efluente',
                },
            ],
            torres,
            localizacao: location || undefined,
            temperaturaAmostra:
                temperatura !== null && Number.isFinite(Number(temperatura))
                    ? Number(temperatura)
                    : null,
        };

        try {
            await onCreate?.(payload);
            onClose?.();
        } catch (e) {
            setError(e?.message || 'Falha ao criar a análise.');
        } finally {
            setSubmitting(false);
        }
    };

    const renderGroup = (titulo, cor, lista) => (
        <Paper
            variant="outlined"
            sx={{
                p: 1.5,
                borderRadius: 2,
                height: '100%',
                bgcolor: alpha(theme.palette[cor].main, 0.03),
                borderColor: alpha(theme.palette[cor].main, 0.25),
            }}
        >
            <Typography
                variant="subtitle2"
                fontWeight={800}
                color={`${cor}.main`}
                sx={{ mb: 1 }}
            >
                {titulo} ({lista.length})
            </Typography>
            {lista.length === 0 ? (
                <Alert severity="warning" variant="outlined" sx={{ py: 0.5 }}>
                    Nenhum dispositivo livre neste grupo.
                </Alert>
            ) : (
                <Stack spacing={1.5}>
                    {lista.map((t) => {
                        const id = towerId(t);
                        return (
                            <Box
                                key={id}
                                sx={{
                                    p: 1,
                                    borderRadius: 1.5,
                                    border: '1px solid',
                                    borderColor: 'divider',
                                    bgcolor: 'background.paper',
                                }}
                            >
                                <Stack
                                    direction="row"
                                    alignItems="center"
                                    spacing={1}
                                    flexWrap="wrap"
                                    useFlexGap
                                    sx={{ mb: 1 }}
                                >
                                    <Typography variant="body2" fontWeight={700}>
                                        SN: {t.numeroSerie}
                                    </Typography>
                                    {t.tag && (
                                        <Chip
                                            label={t.tag}
                                            size="small"
                                            sx={{
                                                bgcolor: t.tagColor || '#9e9e9e',
                                                color: '#fff',
                                                height: 20,
                                                fontSize: '0.7rem',
                                                fontWeight: 'bold',
                                            }}
                                        />
                                    )}
                                </Stack>
                                <DilutionInput
                                    value={dilutions[id] ?? 0}
                                    onChange={(v) => setDilucaoFor(id, v)}
                                    readOnly={submitting}
                                />
                            </Box>
                        );
                    })}
                </Stack>
            )}
        </Paper>
    );

    return (
        <Dialog
            open={open}
            onClose={submitting ? undefined : onClose}
            fullWidth
            maxWidth="sm"
            fullScreen={isMobile}
            PaperProps={{ sx: { borderRadius: isMobile ? 0 : 3 } }}
        >
            <DialogTitle
                sx={{
                    bgcolor: theme.palette.primary.main,
                    color: theme.palette.primary.contrastText,
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                    py: 1.5,
                }}
            >
                <Lucide.Sparkles />
                <Typography variant="h6" component="div" sx={{ flexGrow: 1, fontWeight: 600 }}>
                    Nova Análise
                </Typography>
                <IconButton onClick={onClose} disabled={submitting} sx={{ color: 'inherit' }}>
                    <Lucide.X />
                </IconButton>
            </DialogTitle>

            <DialogContent dividers sx={{ bgcolor: 'background.default', p: { xs: 2, sm: 2.5 } }}>
                {!special ? (
                    <Alert severity="info">
                        Este cliente não está no modo automático (SANEAMENTO com um único
                        protocolo Afluente/Efluente). Use "Nova Análise" padrão.
                    </Alert>
                ) : (
                    <Stack spacing={2}>
                        {/* Resumo: código + temperatura */}
                        <Paper
                            variant="outlined"
                            sx={{ p: 1.5, borderRadius: 2, bgcolor: 'background.paper' }}
                        >
                            <Stack
                                direction={{ xs: 'column', sm: 'row' }}
                                spacing={1.5}
                                divider={<Divider orientation="vertical" flexItem />}
                            >
                                <Stack direction="row" alignItems="center" spacing={1}>
                                    <Lucide.QrCode size={18} color={theme.palette.action.active} />
                                    <Box>
                                        <Typography variant="caption" color="text.secondary">
                                            Código
                                        </Typography>
                                        <Typography variant="body1" fontWeight={800}>
                                            {codigo}
                                        </Typography>
                                    </Box>
                                </Stack>
                                <Stack direction="row" alignItems="center" spacing={1}>
                                    <Lucide.Thermometer size={18} color={theme.palette.action.active} />
                                    <Box>
                                        <Typography variant="caption" color="text.secondary">
                                            Temperatura {locationLabel ? `(${locationLabel})` : ''}
                                        </Typography>
                                        <Typography variant="body1" fontWeight={800}>
                                            {tempLoading ? (
                                                <CircularProgress size={16} />
                                            ) : temperatura !== null ? (
                                                `${temperatura} °C`
                                            ) : (
                                                'automática'
                                            )}
                                        </Typography>
                                    </Box>
                                </Stack>
                            </Stack>
                        </Paper>

                        <Box>
                            <Typography
                                variant="overline"
                                color="text.secondary"
                                fontWeight={700}
                                sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}
                            >
                                <Lucide.Shapes size={18} /> Dispositivos ({totalDispositivos}) —
                                confira a diluição de cada um
                            </Typography>

                            {loadingTowers ? (
                                <Stack alignItems="center" sx={{ py: 3 }}>
                                    <CircularProgress size={24} />
                                    <Typography variant="caption" color="text.secondary" sx={{ mt: 1 }}>
                                        Buscando dispositivos livres...
                                    </Typography>
                                </Stack>
                            ) : (
                                <Grid container spacing={1.5}>
                                    <Grid item xs={12} sm={6}>
                                        {renderGroup('Entrada (Afluente)', 'error', afluente)}
                                    </Grid>
                                    <Grid item xs={12} sm={6}>
                                        {renderGroup('Saída (Efluente)', 'info', efluente)}
                                    </Grid>
                                </Grid>
                            )}
                        </Box>

                        {error && <Alert severity="error">{error}</Alert>}
                        {!loadingTowers &&
                            (afluente.length === 0 || efluente.length === 0) && (
                                <Alert severity="warning">
                                    É preciso ao menos 1 dispositivo livre em cada grupo
                                    (Afluente e Efluente) para criar a análise.
                                </Alert>
                            )}
                    </Stack>
                )}
            </DialogContent>

            <DialogActions sx={{ p: 2, bgcolor: 'background.paper' }}>
                <Button onClick={onClose} disabled={submitting} color="inherit">
                    Cancelar
                </Button>
                <Button
                    onClick={handleCreate}
                    disabled={!canSubmit}
                    variant="contained"
                    disableElevation
                    startIcon={submitting ? <CircularProgress size={16} color="inherit" /> : <Lucide.Check />}
                    sx={{ px: 4 }}
                >
                    Criar análise
                </Button>
            </DialogActions>
        </Dialog>
    );
}
